<template>
  <div class="min-h-screen bg-gray-100 flex flex-col w-full items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
    <div v-if="!payNow" class="w-1/2 bg-white shadow-lg rounded-lg p-8 space-y-6">
      <!-- Header -->
      <div class="text-center">
        <h1 class="text-3xl font-bold text-gray-900">Rafiki360 Payment Required</h1>
        <p class="mt-2 text-sm text-gray-600">
          Your monthly invoice for Rafiki360 is unpaid and overdue. Please make a payment to continue accessing the system.
        </p>
      </div>

      <!-- Invoice Details -->
      <div class="border-t border-gray-200 pt-4">
        <h2 class="text-lg font-semibold text-gray-700">Invoice Details</h2>
        <dl class="mt-2 space-y-2">
          <div class="flex justify-between">
            <dt class="text-sm text-gray-600">Invoice Amount:</dt>
            <dd class="text-sm font-medium text-gray-900">KES {{ loading ? '...' : invoiceAmount.toLocaleString() }}</dd>
          </div>
          <div class="flex justify-between">
            <dt class="text-sm text-gray-600">Due Date:</dt>
            <dd class="text-sm font-medium text-gray-900">{{ loading ? '...' : dueDateFormatted }}</dd>
          </div>
        </dl>
      </div>

      <!-- Payment Form -->
      <div class="space-y-4">
        <div v-if="paymentError" class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded" role="alert">
          <p>{{ paymentError }}</p>
        </div>
        <div v-if="paymentSuccess" class="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded" role="alert">
          <p>Payment successful! Redirecting to dashboard...</p>
        </div>
        <button
            @click="makePayment"
            class="w-full bg-custom-blue text-white font-semibold py-2 px-10 rounded-lg hover:bg-custom-yellow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
        >
          Pay Now
        </button>
      </div>

      <!-- Support Contact -->
      <div class="text-center text-sm text-gray-500">
        <p>Need help? Contact support at <a :href="`tel:+${formatPhoneNumber(supportNumber)}`" class="text-blue-500 hover:underline">{{ formatPhoneNumber(supportNumber) }}</a></p>
      </div>
    </div>

    <div v-else>
      <h2 class="text-xl font-bold mb-4">Payment Method</h2>
      <div class="flex w-full flex-col p-6 bg-white border border-gray-200 rounded-lg">
        <div class="flex w-full">
          <h5 class="font-bold mr-auto">Mpesa</h5>
          <svg class="ml-auto" width="30" height="22" viewBox="0 0 30 22" fill="none" xmlns="http://www.w3.org/2000/svg"
               xmlns:xlink="http://www.w3.org/1999/xlink">
            <rect width="30" height="21.9847" fill="url(#pattern0_8173_56145)"/>
            <defs>
              <pattern id="pattern0_8173_56145" patternContentUnits="objectBoundingBox" width="1" height="1">
                <use xlink:href="#image0_8173_56145" transform="scale(0.00381679 0.00520833)"/>
              </pattern>
              <image id="image0_8173_56145" width="262" height="192" preserveAspectRatio="none"
                     xlink:href="data:image/png;base64,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"/>
            </defs>
          </svg>
        </div>
        <p class="mt-5">
          An Mpesa payment request of KES {{ paymentAmount }} will be sent to your phone number, please ensure you have
          enough money
          to complete the payment.
        </p>
        <h5 class="font-bold mt-4 text-sm">Mpesa Number</h5>
        <div class="max-w-xl  mt-3">
          <div class="flex items-center">
            <div
                class="shrink-0 z-10 inline-flex items-center py-2.5 px-4 text-sm font-medium text-center text-gray-900 border border-gray-300 rounded-s-lg hover:bg-gray-200 focus:ring-4 focus:outline-none focus:ring-gray-100">
              +254
            </div>

            <div class="relative w-full">
              <input
                  v-model="phoneNumber"
                  type="text" id="phone-input"
                  class="block p-2.5 w-full z-20 text-sm text-gray-900 rounded-e-lg border-s-0 border border-gray-300 focus:ring-blue-500 focus:border-blue-500  "
                  pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}" placeholder="e.g 712 xxx xxx" required/>
            </div>

          </div>
        </div>
        <div class="flex items-center justify-center mt-4 gap-2">
          <button @click="proceedToPayment" class="px-10 py-2 b rounded-md"
                  :class="promptingPayment ? 'bg-gray-200 border-custom-blue': 'bg-custom-blue text-white' ">
            {{ initialPromptSent ? (promptingPayment ? 'Resending prompt...' : 'Resend Prompt') : (promptingPayment ? 'Sending prompt...' : 'Send Prompt') }}
          </button>

          <button v-if="initialPromptSent" class="text-white bg-green-600 py-2 px-4" @click.prevent="confirmPayment">
            {{ confirmingPayment ? 'Confirming ...' : 'Confirm Payment' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, computed} from 'vue';
import apiClient from "@/services/api";
import {useToast} from "vue-toast-notification";

const billDue = ref({ total_amount: 0, id: null, month: '' }); // Initialize with default values
const supportNumber = ref('+254718614580'); // Mock support number
const paymentError = ref('');
const paymentSuccess = ref(false);
const payNow = ref(false);
const loading = ref(false);
const promptingPayment = ref(false);
const initialPromptSent = ref(false);
const confirmingPayment = ref(false);
const toast = useToast(); // Assuming you have a toast notification system
const phoneNumber = ref('');
const invoiceAmount = computed(() => billDue.value.total_amount || 0);
const dueDateFormatted = computed(() => {
  const date = new Date(billDue.value.month);
  // Set to next month and day 0 to get last day of current month
  date.setMonth(date.getMonth() + 1);
  date.setDate(0);
  // Return the day
  return date.getDate() + ' ' + date.toLocaleString('default', { month: 'long' }) + ' ' + date.getFullYear();
});
// Phone number formatter
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return 'N/A';
  let cleaned = phoneNumber.toString().replace(/\D/g, '');
  if (cleaned.startsWith('0')) {
    cleaned = '254' + cleaned.slice(1);
  }
  else if (cleaned.startsWith('+254')) {
    cleaned = cleaned.slice(1);
  }
  else if (cleaned.length === 9 && /^[0-1][0-9]{8}$/.test(cleaned)) {
    cleaned = '254' + cleaned;
  }
  else {
    return 'Invalid number';
  }
  return cleaned.length === 12 ? cleaned : 'Invalid number';
};

// Mock payment function
const makePayment = () => {
  payNow.value = true;
};

const getPendingBill=  async () => {
  console.log("Fetching pending bill...");
  try {
    loading.value = true;
    const response = await apiClient.fetchBillDue();
    if (response.success) {
      billDue.value = response.data;
      console.log("Bill Due:", billDue.value);
    } else {
      this.$toast.error('Failed to fetch bill due');
    }
  } catch (error) {
    loading.value = false;
    this.$toast.error('Failed to register patient');
    console.log(error);
  } finally {
    loading.value = false;
  }
};

/**
 * Formats a Kenyan phone number to the standard +254 format.
 * @param phoneNumber
 * @returns {string}
 */
const formatKenyanPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return 'N/A';
  let cleaned = phoneNumber.toString().replace(/\D/g, '');
  if (cleaned.startsWith('0')) {
    cleaned = '254' + cleaned.slice(1);
  }
      // else if (cleaned.startsWith('254')) {
      //   cleaned = cleaned;
  // }
  else if (cleaned.startsWith('+254')) {
    cleaned = cleaned.slice(1);
  }
  else if (cleaned.length === 9 && /^[0-1][0-9]{8}$/.test(cleaned)) {
    cleaned = '254' + cleaned;
  }
  else {
    return 'Invalid number';
  }
  if (cleaned.length !== 12) {
    return 'Invalid number';
  }
  return cleaned;
};


const proceedToPayment = async () => {
  if (!phoneNumber.value) {
    alert('Please enter a valid phone number.');
    return;
  }
  try {
    promptingPayment.value = true;
    let response = await apiClient.initiatePaymentStkPush({
      amount: billDue.value.total_amount,
      phoneNumber: formatKenyanPhoneNumber(phoneNumber.value),
      bill_id: billDue.value.id,
    });
    if (response.success) {
      toast.success("Payment prompt sent successfully. Please check your phone.");
      setTimeout(() => {
        promptingPayment.value = false;
        initialPromptSent.value = true;
        phoneNumber.value = ''; // Clear the phone number input
      }, 3000); // Reset after 3 seconds
    } else {
      promptingPayment.value = false;
      console.error("Failed to fetch system billing list:", response);
      toast.error(response.message);
    }
  }catch (e) {
    promptingPayment.value = false;
    console.error("Failed to fetch system billing list:", e);
    toast.error("Failed to fetch system billing list");
  }
};

const confirmPayment = async () => {
  try {
    confirmingPayment.value = true;
    const response = await apiClient.confirmBillPaymentStatus(billDue.value.id);
    if (response.success) {
      toast.success("Payment received successfully.");
    } else {
      console.error("Failed to confirm payment:", response);
      toast.error('Payment not yet received.');
    }
    confirmingPayment.value = false;
  } catch (e) {
    confirmingPayment.value = false;
    console.error("Failed to confirm payment:", e);
    toast.error("Failed to confirm payment");
  }
}

// Fetch invoice data on mount (mocked)
onMounted(async () => {
  await getPendingBill();
  // Replace with API call to your Rails backend
  // e.g., fetch('/api/invoices/unpaid')
  // Update invoiceAmount, dueDate, institutionName, contactNumber
});

</script>